import { ChangeDetectionStrategy, Component, computed, inject, input, signal } from '@angular/core';

import { BrandingService } from '../../../core/branding/branding.service';
import { officeInitials } from '../../../core/branding/office-initials.util';

/**
 * Marca do escritório: logo enviada no Perfil do escritório; senão as iniciais do nome; sem
 * perfil cadastrado, o símbolo do sistema. `tone="ink"` é para fundos azul-marinho.
 */
@Component({
  selector: 'app-office-mark',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (logoUrl(); as url) {
      <img class="mark is-logo" [src]="url" alt="" (error)="failedUrl.set(url)" />
    } @else if (initials(); as initials) {
      <span class="mark is-initials" aria-hidden="true">{{ initials }}</span>
    } @else {
      <span class="mark" aria-hidden="true">
        <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M3 12L8 4l5 8H3z" /></svg>
      </span>
    }
  `,
  styles: `
    :host { display: inline-flex; flex-shrink: 0; --mark-size: 34px; }
    :host([size='lg']) { --mark-size: 44px; }

    .mark {
      width: var(--mark-size);
      height: var(--mark-size);
      display: grid;
      place-items: center;
      border-radius: calc(var(--mark-size) * 0.22);
      border: 1.5px solid var(--mark-line, var(--color-primary));
      color: var(--mark-line, var(--color-primary));
    }

    :host([tone='ink']) .mark { --mark-line: var(--color-brand-brass); }

    .mark svg { width: 42%; height: 42%; }

    .is-initials {
      font-family: var(--font-display);
      font-size: calc(var(--mark-size) * 0.4);
      font-weight: 600;
      letter-spacing: 0.02em;
      line-height: 1;
    }

    /* Logo sobre fundo claro, para não depender da cor do arquivo enviado. */
    .is-logo {
      border: 1px solid var(--color-border);
      background: #fff;
      padding: 3px;
      object-fit: contain;
    }

    :host([tone='ink']) .is-logo { border-color: transparent; }
  `,
  host: {
    '[attr.size]': 'size()',
    '[attr.tone]': 'tone()',
  },
})
export class OfficeMarkComponent {
  protected readonly branding = inject(BrandingService);

  readonly size = input<'md' | 'lg'>('md');
  readonly tone = input<'light' | 'ink'>('light');
  /** Nome em edição (prévia): as iniciais acompanham o que está sendo digitado. */
  readonly name = input<string | null>(null);

  protected readonly initials = computed(() => {
    const name = this.name();
    return name !== null ? officeInitials(name) : this.branding.initials();
  });

  /** Se a imagem falhar (ex.: removida no storage), volta para as iniciais até trocar a logo. */
  protected readonly failedUrl = signal<string | null>(null);
  protected readonly logoUrl = computed(() => {
    const url = this.branding.logoUrl();
    return url && url !== this.failedUrl() ? url : null;
  });
}
